import React, { useCallback } from "react";
import {
  Typography,
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Switch,
  Stack,
  Card,
  Divider,
} from "@mui/material";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import SearchIcon from "@mui/icons-material/Search";
import PermissionAccordionList from "../../Lists/PermissionsAccordionList/permission-accordion-list";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { produce, immerable } from "immer";
import { orderService, userService } from "../../../Core/Api/services";
import { CreateGroupRoleRequest } from "../../../Core/Api/Types/create-group-role-request";
import { ColorType } from "../../../Core/Api/Types/color-type";
import CopyPermissionPopperRole from "../../Misc/CopyPermissionPopper/copy-permission-role-popper";
import CopyPermissionPopperGroup from "../../Misc/CopyPermissionPopper/copy-permission-group-popper";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import ConfigGroupAssociations from "../../Config/ConfigGroupAssociations/config-group-associations";
import ConfigContextLevelSettings from "../../Config/ConfigContextLevelSettings/config-context-level-settings";
import ConfigFiltering from "../../Config/ConfigFiltering/config-filtering";
import ConfigMediaShareInfos from "../../Config/ConfigMediaShareInfos/config-media-share-infos";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { AclWorkflowAccessLevel } from "../../../Core/Api/Types/acl-workflow-access-level";
import { AclAssetAccessLevel } from "../../../Core/Api/Types/acl-asset-access-level";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { ModalContext } from "../../Modals";
import ModalSearchQueryBuilder, {
  ModalSearchQueryBuilderElementProps,
} from "../../Modals/ModalSearchQueryBuilder/modal-search-query-builder";
import { useTranslation } from "react-i18next";
import { minutesToTimeSpan } from "../../../Utils/helpers";

export interface EditUserRolePanelProps {
  width?: number;
  open: boolean;
  role: DbUserGroupRoleInfos | null;
  allRoles: DbUserGroupRoleInfos[];
  onConfirm: (roles: DbUserGroupRoleInfos, isCreation: boolean) => void;
  onClose: React.MouseEventHandler;
}

const EditUserRolePanel: React.FC<EditUserRolePanelProps> = (props) => {
  const { t } = useTranslation();
  const { role, allRoles, onClose, open, onConfirm } = props;
  const [groupRole, setGroupRole] = React.useState<DbUserGroupRoleInfos>(new DbUserGroupRoleInfos());
  const [acls, setAcls] = React.useState<AclTypes[]>([]);
  const [overwriteAcls, setOverwriteAcls] = React.useState<boolean>(false);
  const [overwriteConf, setOverwriteConf] = React.useState<boolean>(false);
  const [permissionAnchorRole, setPermissionAnchorRole] = React.useState<HTMLButtonElement>();
  const [permissionAnchorGroup, setPermissionAnchorGroup] = React.useState<HTMLButtonElement>();
  const [currencies, setCurrencies] = React.useState<CurrencyTypes[] | null>();

  const enqueueCustomSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const onClickCopyPermissionsRole: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    setPermissionAnchorRole((prevState) => (prevState ? undefined : e.currentTarget));
  };

  const onClickCopyPermissionsGroup: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    setPermissionAnchorGroup((prevState) => (prevState ? undefined : e.currentTarget));
  };

  const onClickCopyPermissionsRoleValidation = (aclsToSet: AclTypes[] | null | undefined) => {
    if (aclsToSet) {
      setAcls(aclsToSet);
      console.debug("acls copied from role:", aclsToSet);
    }
    setPermissionAnchorRole(undefined);
  };

  const onClickCopyPermissionsGroupValidation = (aclsToSet: AclTypes[] | null | undefined) => {
    if (aclsToSet) {
      setAcls(aclsToSet);
      console.debug("acls copied from group:", aclsToSet);
    }
    setPermissionAnchorGroup(undefined);
  };

  const onClickSave = useCallback(() => {
    if (role === null && groupRole)
      userService
        .CreateGroupRole(
          new CreateGroupRoleRequest({
            groupRole: {
              [immerable]: true,
              acls: acls,
              color: groupRole.color,
              description: groupRole.description,
              isNew: true,
              name: groupRole.name,
              platformId: groupRole.platformId,
              id: groupRole.id,
              isSystem: false,
              currency: groupRole!.currency,
              aclWorkflowAccessLevel: groupRole!.aclWorkflowAccessLevel,
              aclAssetAccessLevel: groupRole!.aclAssetAccessLevel,
              allowedObjectClasses: groupRole!.allowedObjectClasses,
              filterTitleBusinessUnits: groupRole!.filterTitleBusinessUnits,
              customFilters: groupRole!.customFilters,
              maxShareViewDays: groupRole!.maxShareViewDays,
              maxShareViews: groupRole!.maxShareViews,
              maxShareViewIPs: groupRole!.maxShareViewIPs,
              maxShareViewClipsNumber: groupRole!.maxShareViewClipsNumber,
              maxShareViewClipsDuration: groupRole!.maxShareViewClipsDuration,
              defaultShareViewDays: groupRole!.defaultShareViewDays,
              defaultShareViews: groupRole!.defaultShareViews,
              defaultShareViewIPs: groupRole!.defaultShareViewIPs,
              defaultShareViewClipsNumber: groupRole!.defaultShareViewClipsNumber,
              defaultShareViewClipsDuration: groupRole!.defaultShareViewClipsDuration,
              defaultShareName: groupRole!.defaultShareName,
            },
            overwriteGroupAcls: overwriteAcls,
            overwriteGroupConf: overwriteConf,
          })
        )
        .then(function (response) {
          if (response.success) {
            let role = Object.assign({}, groupRole);
            role.id = response.objectId!;
            onConfirm(role, true);
          } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
        });
    else
      userService
        .UpdateGroupRole(
          new CreateGroupRoleRequest({
            groupRole: {
              [immerable]: true,
              acls: acls,
              color: groupRole.color,
              description: groupRole.description,
              isNew: false,
              name: groupRole.name,
              platformId: groupRole.platformId,
              id: groupRole.id,
              isSystem: groupRole.isSystem || false,
              currency: groupRole!.currency,
              aclWorkflowAccessLevel: groupRole!.aclWorkflowAccessLevel,
              aclAssetAccessLevel: groupRole!.aclAssetAccessLevel,
              allowedObjectClasses: groupRole!.allowedObjectClasses,
              filterTitleBusinessUnits: groupRole!.filterTitleBusinessUnits,
              customFilters: groupRole!.customFilters,
              maxShareViewDays: groupRole!.maxShareViewDays,
              maxShareViews: groupRole!.maxShareViews,
              maxShareViewIPs: groupRole!.maxShareViewIPs,
              maxShareViewClipsNumber: groupRole!.maxShareViewClipsNumber,
              maxShareViewClipsDuration: groupRole!.maxShareViewClipsDuration,
              defaultShareViewDays: groupRole!.defaultShareViewDays,
              defaultShareViews: groupRole!.defaultShareViews,
              defaultShareViewIPs: groupRole!.defaultShareViewIPs,
              defaultShareViewClipsNumber: groupRole!.defaultShareViewClipsNumber,
              defaultShareViewClipsDuration: groupRole!.defaultShareViewClipsDuration,
              defaultShareName: groupRole!.defaultShareName,
            },
            overwriteGroupAcls: overwriteAcls,
            overwriteGroupConf: overwriteConf,
          })
        )
        .then(function (response) {
          if (response.success) {
            let role = Object.assign({}, groupRole);
            role.acls = acls;
            onConfirm(role, false);
          } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
        });
  }, [acls, groupRole, overwriteAcls, overwriteConf, role]);

  React.useEffect(() => {
    if (role) {
      setGroupRole(role);
      if (role.acls) setAcls(role.acls);
    } else setGroupRole(new DbUserGroupRoleInfos());
  }, [open]);

  React.useEffect(() => {
    orderService.GetCurrencies().then(function (response) {
      if (response.success) setCurrencies(response.currencies);
    });
  }, []);

  const handleRoleContextLevelSettings = (value, context) => {
    setGroupRole(
      produce((draft) => {
        if (context === "aclWorkflowAccessLevel")
          draft!.aclWorkflowAccessLevel = value ? AclWorkflowAccessLevel[value] : null;
        else draft!.aclAssetAccessLevel = value ? AclAssetAccessLevel[value] : null;
      })
    );
  };

  const handleRoleFiltering = (value, context) => {
    setGroupRole(
      produce((draft) => {
        switch (context) {
          case "allowedAssetsClasses":
            draft!.allowedObjectClasses = value ? value.map((x) => ObjectClass[x]) : null;
            break;
          case "filterTitleBusinessUnits":
            draft!.filterTitleBusinessUnits = value;
            break;
          case "filterAssets":
            if (!draft!.customFilters) draft!.customFilters = [];

            if (value.currentTarget && value.currentTarget.value && value.currentTarget.value !== "") {
              if (draft!.customFilters?.find((x) => x.layer === ObjectLayer.Asset))
                draft!.customFilters!.find((x) => x.layer === ObjectLayer.Asset)!.query = value.currentTarget.value;
              else
                draft!.customFilters?.push({
                  layer: ObjectLayer.Asset,
                  query: value.currentTarget.value,
                });
            } else {
              let index = draft!.customFilters?.findIndex((x) => x.layer === ObjectLayer.Asset);
              if (index !== -1) draft!.customFilters.splice(index, 1);
            }
            break;
          case "filterContents":
            if (!draft!.customFilters) draft!.customFilters = [];

            if (value.currentTarget && value.currentTarget.value && value.currentTarget.value !== "") {
              if (draft!.customFilters?.find((x) => x.layer === ObjectLayer.Content))
                draft!.customFilters!.find((x) => x.layer === ObjectLayer.Content)!.query = value.currentTarget.value;
              else
                draft!.customFilters?.push({
                  layer: ObjectLayer.Content,
                  query: value.currentTarget.value,
                });
            } else {
              let index = draft!.customFilters?.findIndex((x) => x.layer === ObjectLayer.Content);
              if (index !== -1) draft!.customFilters.splice(index, 1);
            }
            break;
        }
      })
    );
  };

  const handleModalQB = (layer: ObjectLayer) => {
    openModalQueryBuilder(layer, groupRole?.customFilters?.find((x) => x.layer === layer)?.query);
  };

  const openModalQueryBuilder = (layer: ObjectLayer, query: string | null | undefined) => {
    if (!openModal) return;
    openModal<ModalSearchQueryBuilderElementProps>(ModalSearchQueryBuilder, {
      query: query ?? undefined,
      onValidate: (newQuery: string) => {
        setGroupRole(
          produce((draft) => {
            if (!draft!.customFilters) draft!.customFilters = [];

            if (draft!.customFilters?.find((x) => x.layer === layer))
              draft!.customFilters!.find((x) => x.layer === layer)!.query = newQuery;
            else
              draft!.customFilters?.push({
                layer: layer,
                query: newQuery,
              });
          })
        );
      },
      hideButtons: true,
    });
  };

  const handleRoleMediaShareInfos = (value, context) => {
    setGroupRole(
      produce((draft) => {
        switch (context) {
          case "maxShareViewDays":
            draft!.maxShareViewDays = +value.target.value;
            break;
          case "maxShareViews":
            draft!.maxShareViews = +value.target.value;
            break;
          case "maxShareViewIPs":
            draft!.maxShareViewIPs = +value.target.value;
            break;
          case "maxShareViewClipsNumber":
            draft!.maxShareViewClipsNumber = +value.target.value;
            break;
          case "maxShareViewClipsDuration":
            draft!.maxShareViewClipsDuration = minutesToTimeSpan(value.target.value);
            break;
          case "defaultShareViewDays":
            draft!.defaultShareViewDays = +value.target.value;
            break;
          case "defaultShareViews":
            draft!.defaultShareViews = +value.target.value;
            break;
          case "defaultShareViewIPs":
            draft!.defaultShareViewIPs = +value.target.value;
            break;
          case "defaultShareViewClipsNumber":
            draft!.defaultShareViewClipsNumber = +value.target.value;
            break;
          case "defaultShareViewClipsDuration":
            draft!.defaultShareViewClipsDuration = minutesToTimeSpan(value.target.value);
            break;
          case "defaultShareName":
            draft!.defaultShareName = value.target.value;
        }
      })
    );
  };

  return (
    <Panel
      width={440}
      header={
        <PanelHeader
          onClose={onClose as PanelHeaderProps["onClose"]}
          title={role ? t("panel:title.editRole", { name: role.name }) : t("panel:title.createRole")}
        />
      }
      open={open}
      onClose={onClose}
      footerContent={
        <Stack direction="column" flex={1} spacing={2}>
          {role && (
            <>
              <Card variant="outlined" sx={{ pt: 2, pb: 2, pl: 0.5, pr: 3 }}>
                <FormControlLabel
                  control={<Switch value={overwriteAcls} size="small" />}
                  label={t("field:label.applyPermissionsToGroups")}
                  labelPlacement="start"
                  onChange={(e, value) => setOverwriteAcls(value)}
                />
              </Card>
              <Card variant="outlined" sx={{ pt: 2, pb: 2, pl: 0.5, pr: 3 }}>
                <FormControlLabel
                  control={<Switch value={overwriteConf} size="small" />}
                  label={t("field:label.applyOtherConfigurationToGroups")}
                  labelPlacement="start"
                  onChange={(e, value) => setOverwriteConf(value)}
                />
              </Card>
            </>
          )}
          <Button
            color="primary"
            variant="contained"
            fullWidth
            onClick={onClickSave}
            disabled={
              groupRole.name === null || groupRole.name === "" || groupRole.color === "" || groupRole.color === null
            }
          >
            {role === null ? t("button:createRole") : t("button:saveChanges")}
          </Button>
        </Stack>
      }
    >
      {/*<Box sx={{ pb: 2 }}>
        <Typography color="text.placeholder" mt={1.5}>
          1 group
        </Typography>
    </Box>*/}

      <FormControl sx={{ mb: 2 }}>
        <InputLabel shrink htmlFor="role-title">
          {t("common:name")}
        </InputLabel>
        <OutlinedInput
          id="role-title"
          value={groupRole?.name || ""}
          placeholder={t("field:placeholder.shortRoleName")}
          onChange={(e) =>
            setGroupRole(
              produce((draft) => {
                draft.name = e.target.value;
              })
            )
          }
        />
      </FormControl>
      <FormControl sx={{ mb: 2 }}>
        <InputLabel shrink htmlFor="role-description">
          {t("common:description")}
        </InputLabel>
        <OutlinedInput
          id="role-description"
          value={groupRole?.description || ""}
          placeholder={t("field:placeholder.roleDescription")}
          onChange={(e) =>
            setGroupRole(
              produce((draft) => {
                draft.description = e.target.value;
              })
            )
          }
          multiline
          minRows={50}
        />
      </FormControl>
      <Box>
        <Typography sx={{ mb: "6px" }}>{t("common:color")}</Typography>
        <Select
          displayEmpty
          sx={{ width: "100%" }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          IconComponent={ExpandMoreRoundedIcon}
          value={groupRole.color || ""}
          onChange={(e) =>
            setGroupRole(
              produce((draft) => {
                if (e.target.value) draft.color = e.target.value;
              })
            )
          }
        >
          {Object.values(ColorType).map((color) => (
            <MenuItem value={color} key={color}>
              {Object.keys(ColorType).find((key) => ColorType[key] === color)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Divider sx={{ mt: 2, mb: 2 }} />

      <ConfigGroupAssociations
        groupInfos={groupRole}
        titleIds={[]}
        currencies={currencies || null}
        labProviders={null}
        entities={null}
        context="Role"
        onSetGroupInfos={(value, context) =>
          setGroupRole(
            produce((draft) => {
              draft!.currency = value ? CurrencyTypes[value] : null;
            })
          )
        }
      ></ConfigGroupAssociations>

      <ConfigContextLevelSettings
        groupInfos={groupRole}
        onSetGroupInfos={handleRoleContextLevelSettings}
      ></ConfigContextLevelSettings>

      <ConfigFiltering
        groupInfos={groupRole}
        onSetGroupInfos={handleRoleFiltering}
        onModalQBOpen={handleModalQB}
      ></ConfigFiltering>

      <ConfigMediaShareInfos groupInfos={groupRole} onSetGroupInfos={handleRoleMediaShareInfos}></ConfigMediaShareInfos>
      <Divider sx={{ mt: 2, mb: 2 }} />

      <Typography variant="h5" mb={1.5} mt={3}>
        {t("common:defaultPermissions")}
      </Typography>

      <Stack direction="row" spacing={2} mb={2} alignItems="center" justifyContent="space-between">
        <Typography variant="h6" color="text.tier" mb={1.5}>
          {(acls ? acls.length : "0") + "/" + Object.keys(AclTypes).length}
        </Typography>

        <Button
          size="small"
          onClick={onClickCopyPermissionsGroup}
          endIcon={
            <ExpandMoreRoundedIcon
              sx={{ transform: permissionAnchorGroup ? "rotate(-180deg)" : "none", transition: "transform .3s" }}
            />
          }
        >
          {t("button:copyFromGroup")}
        </Button>

        <Button
          size="small"
          onClick={onClickCopyPermissionsRole}
          endIcon={
            <ExpandMoreRoundedIcon
              sx={{ transform: permissionAnchorRole ? "rotate(-180deg)" : "none", transition: "transform .3s" }}
            />
          }
        >
          {t("button:copyFromRole")}
        </Button>

        <CopyPermissionPopperRole
          anchorEl={permissionAnchorRole}
          allRoles={allRoles}
          onClickConfirm={onClickCopyPermissionsRoleValidation}
          onClose={() => setPermissionAnchorRole(undefined)}
        />
        <CopyPermissionPopperGroup
          anchorEl={permissionAnchorGroup}
          onClickConfirm={onClickCopyPermissionsGroupValidation}
          onClose={() => setPermissionAnchorGroup(undefined)}
        />
      </Stack>
      <OutlinedInput
        label={t("common:aclFilter")}
        placeholder={t("common:aclFilter")}
        sx={{ width: "100%" }}
        endAdornment={
          <InputAdornment position="end">
            <SearchIcon />
          </InputAdornment>
        }
      />
      <PermissionAccordionList searchValue="" acls={acls || []} setAcls={setAcls} />
    </Panel>
  );
};

export default EditUserRolePanel;
